import React, { useEffect } from 'react';
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react';
import { cn } from '../../../utils/cn';

const VARIANT_STYLES = {
  success: {
    container: 'bg-success-50 border-success-200 text-success-900',
    icon: CheckCircle,
    iconColor: 'text-success-600',
  },
  error: {
    container: 'bg-error-50 border-error-200 text-error-900',
    icon: AlertCircle,
    iconColor: 'text-error-600',
  },
  warning: {
    container: 'bg-warning-50 border-warning-200 text-warning-900',
    icon: AlertTriangle,
    iconColor: 'text-warning-600',
  },
  info: {
    container: 'bg-info-50 border-info-200 text-info-900',
    icon: Info,
    iconColor: 'text-info-600',
  },
};

export const Toast = ({
  id,
  variant = 'info',
  title,
  message,
  duration = 5000,
  action,
  onClose,
  isExiting = false,
}) => {
  const styles = VARIANT_STYLES[variant];
  const Icon = styles.icon;
  
  useEffect(() => {
    if (duration === Infinity || isExiting) return;
    
    const timer = setTimeout(() => {
      onClose?.(id);
    }, duration);
    
    return () => clearTimeout(timer);
  }, [duration, id, onClose, isExiting]);
  
  return (
    <div
      className={cn(
        'flex items-start gap-3 p-4 rounded-lg border shadow-lg',
        'min-w-[320px] max-w-md',
        styles.container,
        isExiting ? 'animate-out fade-out slide-out-to-right duration-200' : 'animate-in slide-in-from-right fade-in duration-300'
      )}
      role="alert"
    >
      {/* Icon */}
      <Icon size={20} className={cn('flex-shrink-0 mt-0.5', styles.iconColor)} />
      
      {/* Content */}
      <div className="flex-1 min-w-0">
        {title && (
          <div className="font-semibold text-sm mb-1">
            {title}
          </div>
        )}
        {message && (
          <div className="text-sm">
            {message}
          </div>
        )}
        {action && (
          <button
            onClick={action.onClick}
            className="mt-2 text-sm font-medium underline hover:no-underline"
          >
            {action.label}
          </button>
        )}
      </div>
      
      {/* Close Button */}
      <button
        onClick={() => onClose?.(id)}
        className="flex-shrink-0 p-1 -mt-1 -mr-1 hover:opacity-70 transition-opacity"
        aria-label="Close"
      >
        <X size={16} />
      </button>
    </div>
  );
};

export default Toast;